import React from 'react';
import { useTheme } from '@emotion/react';
import { Box, CircularProgress, Grid, IconButton, Paper, Typography } from '@mui/material';
import { HistoryRounded } from '@mui/icons-material';
import moment from 'moment';
import { ReactComponent as CloseIcon } from "assets/close_icon.svg";
import ModalComponent from 'ui-components/ModalComponent';
import { Secondary } from 'ui-components/Buttons';
import ConfirmationPopup from 'ui-components/popups/DeleteConfirmationPopup';

const summarise = (paths = []) => {
    if (!paths.length) return 'No fields changed';
    const plans = new Set();
    let comparison = 0;
    paths.forEach((path) => {
        if (path.startsWith('plans.')) plans.add(path.split('.')[1]);
        if (path.startsWith('comparison')) comparison += 1;
    });
    const parts = [];
    if (plans.size) parts.push(`${plans.size} plan${plans.size === 1 ? '' : 's'}`);
    if (comparison) parts.push('comparison table');
    return parts.join(' and ') || `${paths.length} field${paths.length === 1 ? '' : 's'}`;
};

/**
 * Every publish keeps the version it replaced. This lists them and puts one
 * back, which is the only way out of a bad save on a page customers are
 * reading right now.
 */
const HistoryPanel = ({ open, handleClose, revisions = [], loading, restoring, onRestore, describeRevision = summarise, restoreWhat = 'The pricing pages' }) => {
    const theme = useTheme();
    const [pending, setPending] = React.useState(null);
    if (!open) return null;

    return (
        <>
            <ModalComponent open={open} onClose={handleClose} style={{ maxWidth: 640, width: '100%' }}>
                <Paper sx={{ background: theme.palette.customColors.whiteBG, borderRadius: '8px' }}>
                    <IconButton sx={{ position: 'absolute', top: 20, right: 20, padding: 0 }} onClick={handleClose}>
                        <CloseIcon width={40} height={40} />
                    </IconButton>

                    <Grid container display="grid" padding={'1.5rem'} gap={'1rem'}>
                        <Grid item xs={12}>
                            <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: theme.palette.customColors.g300 }}>
                                Publish history
                            </Typography>
                            <Typography sx={{ fontSize: '0.875rem', color: theme.palette.customColors.g200, marginTop: 0.5 }}>
                                Restoring brings back the config as it stood before that publish. The restore is itself
                                a publish, so it can be undone the same way.
                            </Typography>
                        </Grid>

                        {loading && (
                            <Grid item xs={12} display="flex" justifyContent="center" padding="1.5rem">
                                <CircularProgress size={'1.5rem'} />
                            </Grid>
                        )}

                        {!loading && revisions.length === 0 && (
                            <Grid item xs={12}>
                                <Typography sx={{ fontSize: '0.875rem', color: theme.palette.customColors.g200 }}>
                                    Nothing published yet from this screen. The first save will appear here.
                                </Typography>
                            </Grid>
                        )}

                        {revisions.map((revision) => (
                            <Grid item xs={12} key={revision._id} sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                flexWrap: 'wrap',
                                padding: '0.75rem 1rem',
                                borderRadius: '8px',
                                border: `1px solid ${theme.palette.customColors.b50}`,
                            }}>
                                <Box>
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                                        {moment(revision.createdAt).format('DD MMM YYYY, HH:mm')}
                                        {revision.restored_from ? ' · restored an older version' : ''}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.8125rem', color: theme.palette.customColors.g200 }}>
                                        {describeRevision(revision.changed_paths, revision)} · by {revision.changed_by || 'unknown'}
                                    </Typography>
                                </Box>
                                <Secondary sx={{ padding: '0.375rem 1rem' }} onClick={() => setPending(revision)}>
                                    Restore this
                                </Secondary>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            </ModalComponent>

            <ConfirmationPopup
                open={Boolean(pending)}
                handleClose={() => setPending(null)}
                handleSubmission={() => {
                    onRestore(pending);
                    setPending(null);
                }}
                heading={'Restore this version'}
                ActionText={restoring ? 'Restoring…' : 'Restore and publish'}
                subheading={
                    pending
                        ? `${restoreWhat} will go back to how they were before the publish on ${moment(pending.createdAt).format('DD MMM, HH:mm')}. Anything changed since is replaced.`
                        : ''
                }
            />
        </>
    );
};

export const HistoryButton = ({ onClick }) => (
    <Secondary startIcon={<HistoryRounded />} onClick={onClick} sx={{ padding: '0.375rem 1rem' }}>
        History
    </Secondary>
);

export default HistoryPanel;
